import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Switch } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { ApiGraphData } from "./SimulationResults";
import { findSeries, getLegendItems, getSignalSeries, Series, STEP_NUMBER_LABEL, STEP_RESULT_LABEL } from "./SimulationGraph";
import { msToSeconds } from "../utils/time";

// Rows have a fixed height so only the rows in view need to be rendered.
const ROW_HEIGHT = 26;
const OVERSCAN_ROWS = 10;

interface Column {
  name: string;
  series: Series;
  kind: "step" | "result" | "signal";
  color?: string;
}

interface Row {
  time: number; // ms
  values: (number | null)[];
  // Whether the column's value changed at this time, rather than being held
  // from an earlier row.
  changed: boolean[];
  failed: boolean;
}

// One row per time at which any column changes. Series are change-compressed,
// so a column holds its last value until its next point.
const buildRows = (columns: Column[]): Row[] => {
  const times = Array.from(new Set(columns.flatMap((column) => column.series.x))).sort((a, b) => a - b);
  const cursors = columns.map(() => -1);
  const resultIndex = columns.findIndex((column) => column.kind === "result");

  return times.map((time) => {
    const values: (number | null)[] = [];
    const changed: boolean[] = [];

    columns.forEach(({ series }, columnIndex) => {
      let cursor = cursors[columnIndex];

      while (cursor + 1 < series.x.length && series.x[cursor + 1] <= time) {
        cursor += 1;
      }

      cursors[columnIndex] = cursor;
      values.push(cursor >= 0 ? series.y[cursor] : null);
      changed.push(cursor >= 0 && series.x[cursor] === time);
    });

    return { time, values, changed, failed: resultIndex >= 0 && values[resultIndex] === 0 };
  });
};

const formatValue = (value: number | null) => value === null ? "" : `${Number(value.toPrecision(10))}`;

const formatCell = (column: Column, value: number | null) => {
  if (column.kind === "result") {
    return value === null ? "" : value ? "Pass" : "Fail";
  }

  return value === null ? "–" : formatValue(value);
};

const escapeCsv = (text: string) => /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;

const downloadCsv = (fileName: string, columns: Column[], rows: Row[]) => {
  const lines = [
    ["Time [s]", ...columns.map((column) => column.name)],
    ...rows.map((row) => [`${msToSeconds(row.time)}`, ...row.values.map(formatValue)]),
  ].map((cells) => cells.map(escapeCsv).join(","));
  const url = URL.createObjectURL(new Blob([`${lines.join("\n")}\n`], { type: "text/csv" }));
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

interface SimulationTableProps {
  data: ApiGraphData;
  // Signals explicitly hidden by the user; missing names are shown.
  traceVisibility: Record<string, boolean>;
  fileName?: string;
}

const SimulationTable: React.FC<SimulationTableProps> = ({ data, traceVisibility, fileName = "results.csv" }) => {
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  // The step columns first, then the signals shown in the legend, in legend order.
  const columns = useMemo<Column[]>(() => {
    const colors = new Map(getLegendItems(data).map((item) => [item.name, item.color]));
    const stepNumber = findSeries(data.results, STEP_NUMBER_LABEL);
    const stepResult = findSeries(data.results, STEP_RESULT_LABEL);

    return [
      ...(stepNumber ? [{ name: "Step", series: stepNumber, kind: "step" as const }] : []),
      ...(stepResult ? [{ name: "Result", series: stepResult, kind: "result" as const }] : []),
      ...Array.from(getSignalSeries(data))
        .filter(([name]) => traceVisibility[name] !== false)
        .map(([name, series]) => ({ name, series, kind: "signal" as const, color: colors.get(name) })),
    ];
  }, [data, traceVisibility]);

  const hasResults = columns.some((column) => column.kind === "result");
  const hasSignals = columns.some((column) => column.kind === "signal");
  const allRows = useMemo(() => buildRows(columns), [columns]);
  const rows = useMemo(
    () => onlyFailed && hasResults ? allRows.filter((row) => row.failed) : allRows,
    [allRows, hasResults, onlyFailed],
  );

  useEffect(() => {
    const element = scrollRef.current;

    if (!element) {
      return;
    }

    const observer = new ResizeObserver(() => setViewportHeight(element.clientHeight));
    observer.observe(element);
    setViewportHeight(element.clientHeight);

    return () => observer.disconnect();
  }, []);

  const firstRow = Math.max(Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN_ROWS, 0);
  const lastRow = Math.min(Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + OVERSCAN_ROWS, rows.length);

  return (
    <div className="cm-table-view">
      <div className="cm-table-toolbar">
        <span className="cm-graph-hint">
          {rows.length} {rows.length === 1 ? "row" : "rows"} · one per change · faded values are held from an earlier row
        </span>
        {hasResults && (
          <Switch
            className="cm-table-switch"
            label="Only failed steps"
            checked={onlyFailed}
            onChange={(event) => setOnlyFailed(event.currentTarget.checked)}
          />
        )}
        <Button
          small
          icon={IconNames.Download}
          disabled={rows.length === 0}
          onClick={() => downloadCsv(fileName, columns, rows)}
        >
          Download CSV
        </Button>
      </div>
      <div
        ref={scrollRef}
        className="cm-table-scroll"
        onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
      >
        {!hasSignals && (
          <p className="cm-table-empty">No signals are shown. Pick some in the Signals pane.</p>
        )}
        <table className="cm-data-table">
          <thead>
            <tr>
              <th>Time [s]</th>
              {columns.map((column) => (
                <th key={column.name}>
                  {column.color && <span className="cm-data-table-swatch" style={{ background: column.color }} />}
                  {column.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {firstRow > 0 && <tr aria-hidden style={{ height: firstRow * ROW_HEIGHT }} />}
            {rows.slice(firstRow, lastRow).map((row) => (
              <tr key={row.time} className={row.failed ? "is-failed" : undefined} style={{ height: ROW_HEIGHT }}>
                <td>{msToSeconds(row.time)}</td>
                {columns.map((column, columnIndex) => (
                  <td
                    key={column.name}
                    className={[
                      row.changed[columnIndex] ? "" : "is-held",
                      column.kind === "result" && row.values[columnIndex] === 0 ? "is-fail" : "",
                    ].join(" ").trim() || undefined}
                  >
                    {formatCell(column, row.values[columnIndex])}
                  </td>
                ))}
              </tr>
            ))}
            {lastRow < rows.length && <tr aria-hidden style={{ height: (rows.length - lastRow) * ROW_HEIGHT }} />}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SimulationTable;
